import React, { useCallback } from 'react'
import {
  FormControl,
  FormLabel,
  Input,
  IconButton,
  Flex,
  Box,
  Text,
} from '@chakra-ui/react'
import { FaTrash } from 'react-icons/fa'
import { ExchangeSymbolCascader } from './ExchangeSymbolCascader'

interface SymbolConfigRowProps {
  id: string
  groupedSymbolData: Record<string, string[]>
  selectedSymbol: string[]
  alias: string
  onSymbolChange: (id: string, field: 'selectedSymbol' | 'alias', value: any) => void
  onRemove: (id: string) => void
  isSubmitted?: boolean
}

export const SymbolConfigRow: React.FC<SymbolConfigRowProps> = React.memo(({
  id,
  groupedSymbolData,
  selectedSymbol,
  alias,
  onSymbolChange,
  onRemove,
  isSubmitted = false,
}) => {
  const handleSymbolSelect = useCallback((selected: string[]) => {
    onSymbolChange(id, 'selectedSymbol', selected)
  }, [id, onSymbolChange])

  const handleAliasChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onSymbolChange(id, 'alias', e.target.value)
  }, [id, onSymbolChange])

  const handleRemove = useCallback(() => {
    onRemove(id)
  }, [id, onRemove])

  return (
    <Box borderWidth="1px" borderRadius="lg" p={4} bg="white">
      <Flex gap={4} alignItems="flex-end">
        <FormControl isRequired flex={1}>
          <FormLabel fontSize="sm" fontWeight="medium" mb={1}>Symbol</FormLabel>
          <Text fontSize="xs" color="gray.500" mb={2}>
            The asset symbol. Enter manually if it’s not in the list.
          </Text>
          <ExchangeSymbolCascader
            groupedData={groupedSymbolData}
            selectedValues={selectedSymbol}
            onChange={handleSymbolSelect}
            placeholder="Select cryptocurrency"
            isInvalid={isSubmitted && selectedSymbol.length === 0}
            isMulti={false}
          />
        </FormControl>

        <FormControl flex={1}>
          <FormLabel fontSize="sm" fontWeight="medium" mb={1}>Alias</FormLabel>
          <Text fontSize="xs" color="gray.500" mb={2}>
            The symbol ID used in your code. Leave empty to use the Symbol itself.
          </Text>
          <Input
            value={alias}
            onChange={handleAliasChange}
            placeholder="Optional alias"
            size="md"
            mb="1px" // Align with the symbol input visually
          />
        </FormControl>

        <IconButton
          aria-label="Remove Symbol"
          icon={<FaTrash />}
          size="md"
          colorScheme="red"
          variant="ghost"
          onClick={handleRemove}
          flexShrink={0}
        />
      </Flex>
    </Box>
  )
})
